import React from "react";
import get from "lodash/get";
import PropTypes from "prop-types";
import { CSSTransition } from "react-transition-group";
import { AccessType, Link, SearchBox, WithMember } from "@quintype/components";
import { Sticky } from "../../basic/sticky";
import { Menu } from "../menu/menu";
import { SidebarTemplate } from "../sidebar/sidebar-template";
import { SocialFollow } from "../../basic/social-follow/social-follow";
import { ImageFallback } from "../../basic/image-fallback.js";
import { UserProfile } from "./user-profile";

import "./header-grid.module.css";
import { breakpoint } from "../../../../utils/breakpoint";

function getMenuData(menu, menuGroup) {
  const filteredMenu = menu.filter(item => item["menu-group-slug"] === menuGroup);
  if (!filteredMenu.length && menuGroup === "sidebar-menu") {
    return getMenuData(menu, "default");
  }
  return filteredMenu;
}

function DrawForm({ children }) {
  return (
    <div>
      <div styleName="internal-search-wrapper">
        <label htmlFor="searchForm" key="1">
          {children}
        </label>
        <div styleName="search-for-open-form">
          <button styleName="search-redirection">
            <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <title>Search</title>
              <path
                d={`M19.71,18.29l-4-4a6.52,
                6.52,0,1,0-1.41,1.41l4,
                4ZM6,10.5A4.5,4.5,0,
                1,1,10.5,15,4.49,4.49,
                0,0,1,6,10.5Z`}
                fill="currentColor"
                fillRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

DrawForm.propTypes = {
  children: PropTypes.func
};

function isSingleLayer(headerConfig) {
  return headerConfig.header_name.slice(0, 12) === "single-layer";
}

export default class HeaderGridTemplate extends React.Component {
  render() {
    const isSSOEnabled = get(this.props, ["publisherTheme", "enableSSO"]) === "true";
    const enableAccesstype = get(this.props, ["publisherTheme", "enableAccesstype"]) === "true";
    const { header_name: headerName, logo, cta, hamburger, login } = this.props.headerConfig;
    const { isSidebarVisible, isSearchFormVisible } = this.props.header;
    const rootMargin =
      isSingleLayer(this.props.headerConfig) || breakpoint("tablet") ? "0px 0px 0px 0px" : "72px 0px 0px 0px";
    const email = get(this.props, ["member", "email"]);
    const phone = get(this.props, ["member", "metadata", "phone-number"], "1234");
    const accessTypeKey = get(this.props, ["publisherTheme", "accesstypeKey"]);
    const isStaging = get(this.props, ["publisherTheme", "accesstypeEnvironment"]) === "staging";

    return (
      <AccessType
        enableAccesstype={enableAccesstype}
        isStaging={isStaging}
        accessTypeKey={accessTypeKey}
        email={email}
        phone={phone}
      >
        {({ initAccessType, initRazorPayPayment }) => (
          <Sticky rootMargin={rootMargin}>
            <div
              className={`${isSingleLayer(this.props.headerConfig) ? "single-layer-override" : "multilayer"}`}
              styleName={`sticky-grid sticky-${headerName} grid-template ${headerName} logo-${logo}-burger-${hamburger} ${
                enableAccesstype ? "grid-override-for-subscription" : " "
              }`}
            >
              <div
                id="logo-wrapper"
                styleName={`logo-wrapper ${logo === "left" ? "logo-margin-left" : ""} ${
                  logo === "center" && cta === "left" ? "logo-center-cta-left" : ""
                }`}
              >
                <Link
                  href="/"
                  callback={() => {
                    this.props.closeAll();
                    this.props.windowScroll();
                  }}
                >
                  {this.props.publisherTheme.secondary_logo ? (
                    <img src={this.props.publisherTheme.secondary_logo} alt="Logo" />
                  ) : (
                    <ImageFallback />
                  )}
                </Link>
              </div>
              <div
                id="secondary-menu-wrapper"
                styleName={`secondary-menu-wrapper ${isSingleLayer(this.props.headerConfig) ? "display-none" : ""} ${
                  logo === "left" ? "custom-menu-style" : ""
                } ${cta === "left" ? "move-right" : "move-left"} ${!isSSOEnabled ? "sso" : " "}`}
              >
                <Menu
                  styleName="menu"
                  menu={getMenuData(this.props.menu, "secondary-menu")}
                  languageDirection={get(this.props, ["publisherTheme", "language_direction"], "left")}
                  count={this.props.publisherTheme["secondary_menu_count"] || 4}
                  toggleSidebar={this.props.toggleSidebar}
                  closeAll={this.props.closeAll}
                />
              </div>
              <div
                id="cta-wrapper"
                styleName={`cta-wrapper ${cta === "left" ? "move-left" : "move-right"} ${!isSSOEnabled ? "sso" : " "} `}
              >
                {enableAccesstype && (
                  <Link href="/subscription" styleName="cta-item btn" data-test-id="subscribe">
                    Subscribe
                  </Link>
                )}
                <SocialFollow
                  hasBackground={true}
                  socialLinks={this.props.socialLinks}
                  color={this.props.publisherTheme["header_icon_color"] || "#000"}
                />
              </div>

              {isSSOEnabled ? (
                <div styleName={`login-btn-wrapper ${login === "left" ? "login-move-left" : "login-move-right"}`}>
                  <WithMember>
                    {({ member, logout, isLoading, checkForMemberUpdated }) => (
                      <UserProfile
                        openLoginTemplate={this.props.openLoginTemplate}
                        closeLoginTemplate={this.props.closeLoginTemplate}
                        member={member}
                        logout={logout}
                        isLoading={isLoading}
                        checkForMemberUpdated={checkForMemberUpdated}
                      />
                    )}
                  </WithMember>
                </div>
              ) : null}

              <div
                id="horizontal-line"
                styleName={`horizontal-line ${isSingleLayer(this.props.headerConfig) ? "display-none" : ""}`}
              />
              <div
                data-test-id="hamburger-icon"
                onClick={this.props.toggleSidebar}
                styleName={`burger-icon ${hamburger === "right" ? "burger-right" : "burger-left"}`}
              >
                {!isSidebarVisible ? (
                  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <title>Hamburger</title>
                    <rect className="cls-1" x="5.5" y="6.5" width="13" height="1" />
                    <polygon points="19 6 5 6 5 8 19 8 19 6 19 6" fill="currentColor" fillRule="evenodd" />
                    <rect className="cls-1" x="5.5" y="11.5" width="13" height="1" />
                    <polygon points="19 11 5 11 5 13 19 13 19 11 19 11" fill="currentColor" fillRule="evenodd" />
                    <rect className="cls-1" x="5.5" y="16.5" width="13" height="1" />
                    <polygon points="19 16 5 16 5 18 19 18 19 16 19 16" fill="currentColor" fillRule="evenodd" />
                  </svg>
                ) : (
                  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <title>Close</title>
                    <polygon
                      points={`16.95 5.64 12 10.59 7.05
                       5.64 5.64 7.05 10.59
                        12 5.64 16.95 7.05
                        18.36 12 13.41 16.95
                        18.36 18.36 16.95 13.41 12
                        18.36 7.05 16.95 5.64
                        16.95 5.64`}
                      fill="currentColor"
                      fillRule="evenodd"
                    />
                  </svg>
                )}
                {
                  <CSSTransition
                    in={isSidebarVisible}
                    timeout={{ enter: 300, exit: 500 }}
                    classNames="side-bar-transition"
                  >
                    <div>
                      <SidebarTemplate
                        customCssClasses={isSingleLayer(this.props.headerConfig) ? "single-layer-override" : ""}
                        isSidebarVisible={isSidebarVisible}
                        headerConfig={this.props.headerConfig}
                        closeSidebar={this.props.toggleSidebar}
                        closeAll={this.props.closeAll}
                        menuItems={getMenuData(this.props.menu, "sidebar-menu")}
                        publisherName={this.props.publisherName}
                        languageDirection={get(this.props, ["publisherTheme", "language_direction"], "left")}
                        socialLinks={this.props.socialLinks}
                        publisherTheme={this.props.publisherTheme}
                      />
                    </div>
                  </CSSTransition>
                }
              </div>
              <div
                id="monogram"
                styleName={`monogram ${isSingleLayer(this.props.headerConfig) ? "display-none" : ""} ${
                  hamburger === "right" ? "move-monogram-right" : ""
                }`}
              >
                {
                  <Link
                    href="/"
                    callback={() => {
                      this.props.closeAll();
                      this.props.windowScroll();
                    }}
                  >
                    {this.props.publisherTheme.primary_logo ? (
                      <img src={this.props.publisherTheme.primary_logo} alt="Monogram" />
                    ) : (
                      <ImageFallback />
                    )}
                  </Link>
                }
              </div>
              <div
                styleName={`primary-menu-wrapper ${logo === "center" && cta === "left" ? "align-menu-right" : ""} ${
                  hamburger === "right" ? "space-left" : ""
                } ${logo === "left" ? "menu-pos-left" : "menu-pos-center"}`}
              >
                <Menu
                  styleName="menu"
                  menu={getMenuData(this.props.menu, "default")}
                  languageDirection={get(this.props, ["publisherTheme", "language_direction"], "left")}
                  count={this.props.publisherTheme["primary_menu_count"] || 6}
                  closeAll={this.props.closeAll}
                />
              </div>
              <div styleName="search-wrapper">
                <div
                  data-test-id="search-icon"
                  styleName={`search-icon ${hamburger === "right" ? "search-position" : ""} ${
                    isSearchFormVisible ? "highlight-search" : ""
                  }`}
                  onClick={this.props.toggleSearchForm}
                >
                  <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <title>Search</title>
                    <path
                      d={`M19.71,18.29l-4-4a6.52,
                      6.52,0,1,0-1.41,1.41l4,
                      4ZM6,10.5A4.5,4.5,0,1,
                      1,10.5,15,4.49,4.49,0,
                      0,1,6,10.5Z`}
                      fill="currentColor"
                      fillRule="evenodd"
                    />
                  </svg>
                </div>
                {!isSearchFormVisible ? null : (
                  <div>
                    <SearchBox
                      styleName={`search-form search-form--visible`}
                      inputId="searchForm"
                      inputClassName="search-form-input"
                      placeholder="Search Here"
                      onSubmitHandler={this.props.closeAll}
                      onEscape={this.props.hideSearchForm}
                      template={DrawForm}
                    />
                    <button styleName="close-btn-svg-wrapper" onClick={this.props.hideSearchForm}>
                      <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <title>Close</title>
                        <polygon
                          points={`16.95 5.64 12 10.59 7.05 5.64 5.64 7.05
                          10.59 12 5.64 16.95 7.05
                          18.36 12 13.41 16.95 18.36
                          18.36 16.95 13.41 12 18.36
                          7.05 16.95 5.64 16.95 5.64`}
                          fill="currentColor"
                          fillRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Sticky>
        )}
      </AccessType>
    );
  }
}

HeaderGridTemplate.propTypes = {
  headerConfig: PropTypes.shape({
    header_name: PropTypes.string,
    logo: PropTypes.string,
    cta: PropTypes.string,
    hamburger: PropTypes.string,
    monogram: PropTypes.bool,
    login: PropTypes.string
  }),
  header: PropTypes.shape({
    isSidebarVisible: PropTypes.bool,
    isSearchFormVisible: PropTypes.bool
  }),
  menu: PropTypes.array,
  openLoginTemplate: PropTypes.func,
  closeLoginTemplate: PropTypes.func,
  publisherTheme: PropTypes.shape({
    primary_logo: PropTypes.string,
    secondary_logo: PropTypes.string,
    primary_menu_count: PropTypes.string,
    secondary_menu_count: PropTypes.string,
    monogram: PropTypes.string,
    header_theme: PropTypes.string,
    header_icon_color: PropTypes.string
  }),
  publisherName: PropTypes.string,
  socialLinks: PropTypes.shape({
    "facebook-url": PropTypes.string,
    "twitter-url": PropTypes.string
  }),
  windowScroll: PropTypes.func,
  toggleSidebar: PropTypes.func,
  hideSearchForm: PropTypes.func,
  toggleSearchForm: PropTypes.func,
  closeAll: PropTypes.func
};
